import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, type CurrentUser } from '@/lib/api'

// Exported so `defineStore`'s inferred public type can reference it — prior
// code declared the interface inside the setup closure which triggered
// TS4025 (exported store uses a private name) when .vue.d.ts shadow files
// were generated during build (F-005).
export interface LoginResponse {
  mfa_required: boolean
  pending_id: string | null
  user: CurrentUser | null
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const loaded = ref(false)

  const isAuthenticated = computed(() => user.value !== null)

  async function bootstrap(): Promise<void> {
    if (loaded.value) return
    try {
      const { data } = await api.get<CurrentUser>('/auth/me')
      user.value = data
    } catch {
      user.value = null
    } finally {
      loaded.value = true
    }
  }

  /** Returns { mfa_required: true, pending_id } if server wants a TOTP code.
   * Otherwise user is logged in and user.value is populated.
   *
   * `captchaToken` is the Cloudflare Turnstile response — pass an empty
   * string when CAPTCHA isn't enabled on the platform. The backend
   * verifies the token only when its secret is configured. */
  async function login(
    email: string,
    password: string,
    captchaToken: string = '',
  ): Promise<LoginResponse> {
    const body: Record<string, unknown> = { email, password }
    if (captchaToken) body.captcha_token = captchaToken
    const { data } = await api.post<LoginResponse>('/auth/login', body)
    if (!data.mfa_required && data.user) {
      user.value = data.user
      loaded.value = true
    }
    return data
  }

  async function verifyMfaChallenge(pending_id: string, code: string): Promise<void> {
    const { data } = await api.post<CurrentUser>('/auth/mfa/verify-challenge', { pending_id, code })
    user.value = data
    loaded.value = true
  }

  async function register(payload: {
    email: string
    name: string
    organization_name: string
    captcha_token?: string
  }): Promise<'ok' | 'check_email'> {
    const res = await api.post<CurrentUser>('/auth/register', payload)
    // 202 = always for public signup: the emailed link finishes it (no enumeration).
    if (res.status === 202) return 'check_email'
    user.value = res.data
    loaded.value = true
    return 'ok'
  }

  /** One-click demo: backend makes a throwaway org and logs us into it. */
  async function startDemo(captchaToken: string = ''): Promise<void> {
    const { data } = await api.post<CurrentUser>('/auth/demo', captchaToken ? { captcha_token: captchaToken } : {})
    user.value = data
    loaded.value = true
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } finally {
      user.value = null
    }
  }

  async function fetchMe(): Promise<void> {
    const { data } = await api.get<CurrentUser>('/auth/me')
    user.value = data
  }

  async function acceptTerms(): Promise<void> {
    const { data } = await api.post<CurrentUser>('/auth/accept-terms', {
      terms_version: user.value?.terms_version ?? '',
      dpa_version: user.value?.dpa_version,
    })
    user.value = data
  }

  async function stopImpersonation(): Promise<void> {
    await api.post('/admin/stop-impersonation')
    await fetchMe()
  }

  /** Passwordless login via passkey. Uses discoverable credentials — browser
   * shows passkey chooser without the user typing an email. */
  async function loginWithPasskey(): Promise<void> {
    const { startAuthentication } = await import('@simplewebauthn/browser')
    const { data: options } = await api.post<PublicKeyCredentialRequestOptionsJSON & { _challenge_id?: string }>('/auth/passkeys/authenticate/start')
    const challengeId = options._challenge_id
    delete options._challenge_id
    const credential = await startAuthentication({ optionsJSON: options })
    const { data } = await api.post<CurrentUser>('/auth/passkeys/authenticate/finish', {
      challenge_id: challengeId,
      credential,
    })
    user.value = data
    loaded.value = true
  }

  return {
    user, isAuthenticated, loaded,
    bootstrap, login, verifyMfaChallenge, loginWithPasskey,
    register, startDemo, logout, fetchMe, stopImpersonation, acceptTerms,
  }
})
